(() => {
  const READY_FLAG = 'smmBundleReady';

  const moneyFormatter = (currency) => {
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EGP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }
  };

  class SMMBundle {
    constructor(root) {
      this.root = root;
      this.form = document.getElementById(root.dataset.productFormId);
      if (!this.form) return;

      this.currency = root.dataset.currency || window.Shopify?.currency?.active || 'EGP';
      this.formatter = moneyFormatter(this.currency);
      this.discount2 = Number(root.dataset.discount2 || 10);
      this.discount3 = Number(root.dataset.discount3 || 15);
      this.productTitle = root.dataset.productTitle || 'Current product';
      this.tier = 1;
      this.selections = [];
      this.activeSlot = null;
      this.submitting = false;

      this.tierButtons = [...root.querySelectorAll('[data-smm-tier]')];
      this.slotsNode = root.querySelector('[data-smm-slots]');
      this.chooser = root.querySelector('[data-smm-chooser]');
      this.productsNode = root.querySelector('[data-smm-products]');
      this.progressNode = root.querySelector('[data-smm-progress]');
      this.searchInput = root.querySelector('[data-smm-search]');
      this.completeNode = root.querySelector('[data-smm-complete]');
      this.errorNode = root.querySelector('[data-smm-error]');
      this.oneEachNode = root.querySelector('[data-smm-one-each]');
      this.subtotalNode = root.querySelector('[data-smm-subtotal]');
      this.savingNode = root.querySelector('[data-smm-saving]');
      this.totalNode = root.querySelector('[data-smm-total]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.submitButton?.querySelector('span');
      this.originalSubmitLabel = this.submitLabel?.textContent?.trim() || '';
      this.baseVariants = this.parseJSON(root.querySelector('[data-smm-current-variants]')?.textContent, []);

      this.bindEvents();
      this.sortCards();
      this.setTier(1, { scroll: false });
      this.observeMainVariant();
    }

    parseJSON(value, fallback) {
      try {
        return JSON.parse(value || '');
      } catch (error) {
        return fallback;
      }
    }

    formatMoney(cents) {
      return this.formatter.format((Number(cents) || 0) / 100);
    }

    discountForTier() {
      if (this.tier === 2) return this.discount2;
      if (this.tier === 3) return this.discount3;
      return 0;
    }

    priceState(price, compareAt, discount = this.discountForTier()) {
      const current = Math.max(0, Number(price) || 0);
      const compare = Math.max(0, Number(compareAt) || 0);
      const reference = compare > current ? compare : current;

      if (!discount) {
        return {
          reference,
          adjusted: current,
          saving: Math.max(0, reference - current),
        };
      }

      const tierTarget = Math.round(reference * (1 - discount / 100));
      const adjusted = Math.min(current, tierTarget);

      return {
        reference,
        adjusted,
        saving: Math.max(0, reference - adjusted),
      };
    }

    bindEvents() {
      this.tierButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.setTier(Number(button.dataset.smmTier), { scroll: true });
        });
      });

      this.root.querySelector('[data-smm-cancel]')?.addEventListener('click', () => {
        this.setTier(1, { scroll: false });
        this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      this.searchInput?.addEventListener('input', () => this.filterCards());

      this.productsNode?.addEventListener('change', (event) => {
        if (!event.target.matches('[data-smm-card-variant]')) return;
        this.updateCardPrice(event.target.closest('[data-smm-card]'));
      });

      this.productsNode?.addEventListener('click', (event) => {
        const chooseButton = event.target.closest('[data-smm-choose]');
        if (!chooseButton) return;
        const card = chooseButton.closest('[data-smm-card]');
        if (card) this.chooseCard(card);
      });

      this.slotsNode?.addEventListener('change', (event) => {
        const select = event.target.closest('[data-smm-slot-variant]');
        if (!select) return;
        const index = Number(select.dataset.smmSlotVariant);
        if (!this.selections[index]) return;
        this.selections[index].variantId = select.value;
        this.updateSummary();
      });

      this.slotsNode?.addEventListener('click', (event) => {
        const changeButton = event.target.closest('[data-smm-change-slot]');
        if (changeButton) {
          this.activeSlot = Number(changeButton.dataset.smmChangeSlot);
          this.showChooserForActiveSlot();
          return;
        }

        const removeButton = event.target.closest('[data-smm-remove-slot]');
        if (removeButton) {
          const index = Number(removeButton.dataset.smmRemoveSlot);
          this.selections[index] = null;
          this.activeSlot = index;
          this.renderSlots();
          this.updateSummary();
          this.showChooserForActiveSlot();
          return;
        }

        const emptyButton = event.target.closest('[data-smm-empty-slot]');
        if (emptyButton) {
          this.activeSlot = Number(emptyButton.dataset.smmEmptySlot);
          this.showChooserForActiveSlot();
        }
      });

      this.form.addEventListener(
        'submit',
        (event) => {
          if (this.tier === 1) return;
          this.submitBundle(event);
        },
        true
      );
    }

    observeMainVariant() {
      const productInfo = this.root.closest('product-info') || this.root.closest('.product') || this.root.parentElement;
      if (!productInfo) return;

      productInfo.addEventListener('change', (event) => {
        if (event.target.closest('[data-smm-root]')) return;
        window.setTimeout(() => {
          this.renderSlots();
          this.updateSummary();
        }, 40);
        window.setTimeout(() => {
          this.renderSlots();
          this.updateSummary();
        }, 250);
      });
    }

    setTier(tier, options = {}) {
      if (![1, 2, 3].includes(tier)) return;

      this.tier = tier;
      const required = this.requiredExtras();

      if (tier === 1) {
        this.selections = [];
        this.activeSlot = null;
        if (this.searchInput) this.searchInput.value = '';
        this.filterCards();
      } else {
        this.selections = this.selections.slice(0, required);
        this.activeSlot = this.firstMissingSlot();
      }

      this.tierButtons.forEach((button) => {
        const active = Number(button.dataset.smmTier) === tier;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      const complete = tier > 1 && this.firstMissingSlot() === null;
      if (this.chooser) this.chooser.hidden = tier === 1 || complete;
      if (this.oneEachNode) this.oneEachNode.hidden = tier === 1;

      this.setDynamicCheckout(tier === 1);
      this.setSubmitLabel(tier === 1 ? this.originalSubmitLabel : 'Add bundle to cart');
      this.renderSlots();
      this.updateAllCardPrices();
      this.updateSummary();
      this.clearError();

      if (tier > 1 && options.scroll && this.chooser && !this.chooser.hidden) {
        window.setTimeout(() => {
          this.chooser?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 50);
      }

      this.root.dispatchEvent(
        new CustomEvent('smm:tier-change', {
          bubbles: true,
          detail: { tier },
        })
      );
    }

    setSubmitLabel(text) {
      if (this.submitLabel && text) this.submitLabel.textContent = text;
    }

    setDynamicCheckout(show) {
      const dynamicCheckout = this.form.querySelector('.shopify-payment-button');
      if (!dynamicCheckout) return;

      if (!dynamicCheckout.dataset.smmPreviousDisplay) {
        dynamicCheckout.dataset.smmPreviousDisplay = dynamicCheckout.style.display || '__empty__';
      }

      if (show) {
        dynamicCheckout.style.display =
          dynamicCheckout.dataset.smmPreviousDisplay === '__empty__'
            ? ''
            : dynamicCheckout.dataset.smmPreviousDisplay;
      } else {
        dynamicCheckout.style.display = 'none';
      }
    }

    requiredExtras() {
      return Math.max(0, this.tier - 1);
    }

    firstMissingSlot() {
      const required = this.requiredExtras();
      for (let index = 0; index < required; index += 1) {
        if (!this.selections[index]) return index;
      }
      return null;
    }

    isComplete() {
      return this.tier > 1 && this.firstMissingSlot() === null;
    }

    updateCompletionState() {
      if (!this.completeNode) return;
      const showComplete = this.isComplete() && Boolean(this.chooser?.hidden);
      this.completeNode.hidden = !showComplete;
      this.completeNode.textContent = showComplete
        ? `✓ Bundle complete — ${this.tier} items selected. Ready to add to cart.`
        : '';
    }

    showChooserForActiveSlot() {
      if (this.tier === 1 || !this.chooser) return;
      this.chooser.hidden = false;
      if (this.completeNode) this.completeNode.hidden = true;
      this.updateProgress();
      this.chooser.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.searchInput?.focus({ preventScroll: true });
    }

    productDataFromCard(card) {
      const select = card.querySelector('[data-smm-card-variant]');
      if (!select) return null;

      const variants = [...select.options].map((option) => ({
        id: String(option.value),
        title: option.textContent.trim(),
        price: Number(option.dataset.price || 0),
        compareAt: Number(option.dataset.compare || 0),
      }));

      return {
        productId: String(card.dataset.productId),
        title: card.dataset.productTitle || card.querySelector('.smm-product-card__title')?.textContent?.trim() || 'Product',
        variants,
        variantId: String(select.value),
      };
    }

    chooseCard(card) {
      const required = this.requiredExtras();
      if (!required) return;

      let target = this.activeSlot;
      if (target === null || target < 0 || target >= required) {
        target = this.firstMissingSlot();
      }

      if (target === null) {
        this.showError('Choose a bundle piece to replace first.');
        return;
      }

      const selection = this.productDataFromCard(card);
      if (!selection) {
        this.showError('This product has no available option.');
        return;
      }

      this.selections[target] = selection;
      this.activeSlot = this.firstMissingSlot();
      this.renderSlots();
      this.updateSummary();
      this.clearError();

      if (this.activeSlot === null) {
        if (this.chooser) this.chooser.hidden = true;
        if (this.searchInput) this.searchInput.value = '';
        this.filterCards();
        this.updateCompletionState();
        window.setTimeout(() => {
          this.slotsNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
    }

    renderSlots() {
      if (!this.slotsNode) return;
      this.slotsNode.replaceChildren();

      const required = this.requiredExtras();
      if (!required) {
        this.updateProgress();
        return;
      }

      const baseVariant = this.getBaseVariant();
      const baseRow = document.createElement('div');
      baseRow.className = 'smm-slot';
      baseRow.dataset.smmSlot = 'base';

      const baseHeading = document.createElement('div');
      baseHeading.className = 'smm-slot__heading';

      const basePieceLabel = document.createElement('span');
      basePieceLabel.className = 'smm-slot__piece';
      basePieceLabel.textContent = 'Piece 1';

      const baseTitle = document.createElement('strong');
      baseTitle.className = 'smm-slot__title';
      baseTitle.textContent = this.productTitle;

      baseHeading.append(basePieceLabel, baseTitle);

      const baseField = document.createElement('div');
      baseField.className = 'smm-field';

      const baseFieldLabel = document.createElement('span');
      baseFieldLabel.textContent = 'Size / option';

      const baseVariantLabel = document.createElement('strong');
      baseVariantLabel.textContent =
        baseVariant?.title && baseVariant.title !== 'Default Title'
          ? baseVariant.title
          : 'Selected on product page';

      const basePrice = document.createElement('div');
      basePrice.className = 'smm-slot__price';
      basePrice.dataset.smmBasePrice = 'true';

      baseField.append(baseFieldLabel, baseVariantLabel);
      baseRow.append(baseHeading, baseField, basePrice);
      this.slotsNode.appendChild(baseRow);

      for (let index = 0; index < required; index += 1) {
        const selection = this.selections[index];
        const row = document.createElement('div');
        row.className = 'smm-slot';
        row.dataset.smmSlot = String(index);

        const heading = document.createElement('div');
        heading.className = 'smm-slot__heading';

        const pieceLabel = document.createElement('span');
        pieceLabel.className = 'smm-slot__piece';
        pieceLabel.textContent = `Piece ${index + 2}`;
        heading.appendChild(pieceLabel);

        if (!selection) {
          const empty = document.createElement('button');
          empty.type = 'button';
          empty.className = 'smm-slot__empty';
          empty.dataset.smmEmptySlot = String(index);
          empty.textContent = 'Choose a product';
          row.append(heading, empty);
          this.slotsNode.appendChild(row);
          continue;
        }

        const title = document.createElement('strong');
        title.className = 'smm-slot__title';
        title.textContent = selection.title;
        heading.appendChild(title);

        const controls = document.createElement('div');
        controls.className = 'smm-slot__actions';

        const change = document.createElement('button');
        change.type = 'button';
        change.className = 'smm-link-button';
        change.dataset.smmChangeSlot = String(index);
        change.textContent = 'Change';

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'smm-link-button';
        remove.dataset.smmRemoveSlot = String(index);
        remove.textContent = 'Remove';

        controls.append(change, remove);
        heading.appendChild(controls);

        const field = document.createElement('label');
        field.className = 'smm-field';

        const fieldLabel = document.createElement('span');
        fieldLabel.textContent = 'Size / option';

        const select = document.createElement('select');
        select.dataset.smmSlotVariant = String(index);

        selection.variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.textContent = variant.title;
          option.dataset.price = String(variant.price);
          option.dataset.compare = String(variant.compareAt || 0);
          option.selected = String(variant.id) === String(selection.variantId);
          select.appendChild(option);
        });

        const price = document.createElement('div');
        price.className = 'smm-slot__price';
        price.dataset.smmSlotPrice = String(index);

        field.append(fieldLabel, select);
        row.append(heading, field, price);
        this.slotsNode.appendChild(row);
      }

      this.updateProgress();
    }

    updateProgress() {
      if (!this.progressNode) return;
      const required = this.requiredExtras();

      if (!required) {
        this.progressNode.textContent = '';
        this.progressNode.classList.remove('is-complete');
        return;
      }

      const chosenExtras = Array.from({ length: required }, (_, index) => this.selections[index]).filter(Boolean).length;
      const chosenItems = 1 + chosenExtras;
      const complete = chosenItems === this.tier;
      this.progressNode.classList.toggle('is-complete', complete);
      this.progressNode.textContent = complete
        ? `Bundle complete — ${this.tier} items selected.`
        : `${chosenItems} of ${this.tier} items selected`;
    }

    selectionVariant(selection) {
      if (!selection) return null;
      return selection.variants.find((variant) => String(variant.id) === String(selection.variantId)) || null;
    }

    getBaseVariantId() {
      return String(
        this.form.querySelector('[name="id"]')?.value ||
        this.root.dataset.initialVariantId ||
        ''
      );
    }

    getBaseVariant() {
      const id = this.getBaseVariantId();
      return this.baseVariants.find((variant) => String(variant.id) === id) || null;
    }

    updateSummary() {
      const baseVariant = this.getBaseVariant();
      if (!baseVariant) return;

      const items = [
        {
          price: Number(baseVariant.price || 0),
          compareAt: Number(baseVariant.compare_at_price || 0),
        },
      ];

      const required = this.requiredExtras();
      for (let index = 0; index < required; index += 1) {
        const variant = this.selectionVariant(this.selections[index]);
        if (variant) {
          items.push({
            price: variant.price,
            compareAt: variant.compareAt,
          });
        }
      }

      const states = items.map((item) => this.priceState(item.price, item.compareAt));
      const subtotal = states.reduce((sum, item) => sum + item.reference, 0);
      const total = states.reduce((sum, item) => sum + item.adjusted, 0);
      const saving = Math.max(0, subtotal - total);

      if (this.subtotalNode) {
        this.subtotalNode.textContent = this.formatMoney(subtotal);
        this.subtotalNode.classList.toggle('has-saving', saving > 0);
      }
      if (this.savingNode) this.savingNode.textContent = this.formatMoney(saving);
      if (this.totalNode) this.totalNode.textContent = this.formatMoney(total);

      this.updateSlotPrices();
      this.updateProgress();
      this.updateCompletionState();
    }

    renderPriceState(node, price, compareAt) {
      if (!node) return;
      const state = this.priceState(price, compareAt);
      node.replaceChildren();

      if (state.reference > state.adjusted) {
        const compare = document.createElement('s');
        compare.textContent = this.formatMoney(state.reference);
        node.appendChild(compare);
      }

      const priceNode = document.createElement('strong');
      priceNode.textContent = this.formatMoney(state.adjusted);
      node.appendChild(priceNode);

      if (state.saving > 0) {
        const save = document.createElement('span');
        save.textContent = `Save ${this.formatMoney(state.saving)}`;
        node.appendChild(save);
      }
    }

    updateSlotPrices() {
      const baseVariant = this.getBaseVariant();
      const baseNode = this.slotsNode?.querySelector('[data-smm-base-price]');
      if (baseNode && baseVariant) {
        this.renderPriceState(
          baseNode,
          Number(baseVariant.price || 0),
          Number(baseVariant.compare_at_price || 0)
        );
      }

      const required = this.requiredExtras();
      for (let index = 0; index < required; index += 1) {
        const node = this.slotsNode?.querySelector(`[data-smm-slot-price="${index}"]`);
        const variant = this.selectionVariant(this.selections[index]);
        if (!node || !variant) continue;
        this.renderPriceState(node, variant.price, variant.compareAt);
      }
    }

    updateCardPrice(card) {
      if (!card) return;
      const select = card.querySelector('[data-smm-card-variant]');
      const option = select?.selectedOptions?.[0];
      if (!option) return;

      const state = this.priceState(
        Number(option.dataset.price || 0),
        Number(option.dataset.compare || 0)
      );

      const compareNode = card.querySelector('[data-smm-card-compare]');
      const priceNode = card.querySelector('[data-smm-card-price]');
      const saveNode = card.querySelector('[data-smm-card-save]');

      if (compareNode) {
        compareNode.textContent = this.formatMoney(state.reference);
        compareNode.hidden = state.reference <= state.adjusted;
      }
      if (priceNode) priceNode.textContent = this.formatMoney(state.adjusted);
      if (saveNode) {
        saveNode.textContent = state.saving > 0 ? `Save ${this.formatMoney(state.saving)}` : '';
        saveNode.hidden = state.saving <= 0;
      }
    }

    updateAllCardPrices() {
      this.root.querySelectorAll('[data-smm-card]').forEach((card) => this.updateCardPrice(card));
    }

    sortCards() {
      if (!this.productsNode) return;
      const cards = [...this.productsNode.querySelectorAll('[data-smm-card]')];
      cards
        .sort((a, b) => Number(b.dataset.priceMax || 0) - Number(a.dataset.priceMax || 0))
        .forEach((card) => this.productsNode.appendChild(card));
    }

    filterCards() {
      const query = (this.searchInput?.value || '').trim().toLowerCase();
      this.root.querySelectorAll('[data-smm-card]').forEach((card) => {
        const haystack = `${card.dataset.productTitle || ''} ${card.textContent || ''}`.toLowerCase();
        card.hidden = Boolean(query) && !haystack.includes(query);
      });
    }

    showError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message;
      this.errorNode.hidden = false;
    }

    clearError() {
      if (!this.errorNode) return;
      this.errorNode.hidden = true;
      this.errorNode.textContent = '';
    }

    setSubmitting(isSubmitting) {
      this.submitting = isSubmitting;
      if (!this.submitButton) return;

      this.submitButton.classList.toggle('loading', isSubmitting);
      this.submitButton.setAttribute('aria-disabled', isSubmitting ? 'true' : 'false');
      const spinner = this.submitButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isSubmitting);
    }

    validateBundle() {
      const required = this.requiredExtras();
      for (let index = 0; index < required; index += 1) {
        const selection = this.selections[index];
        const variant = this.selectionVariant(selection);
        if (!selection || !variant?.id) {
          return `Choose product and size for Piece ${index + 2}.`;
        }
      }

      if (!this.getBaseVariantId()) return 'Choose an available option for the main product.';
      return '';
    }

    bundleLineProperties(bundleId, slot) {
      return {
        _smm_bundle_id: bundleId,
        _smm_bundle_size: String(this.tier),
        _smm_bundle_slot: slot,
        _smm_bundle_tier: `${this.discountForTier()}%`,
      };
    }

    async submitBundle(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.submitting) return;

      const validationError = this.validateBundle();
      if (validationError) {
        this.showError(validationError);
        if (this.chooser) this.chooser.hidden = false;
        this.updateCompletionState();
        this.chooser?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      this.clearError();
      this.setSubmitting(true);

      const bundleId = `SMM-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const items = [
        {
          id: Number(this.getBaseVariantId()),
          quantity: 1,
          properties: this.bundleLineProperties(bundleId, 'base'),
        },
      ];

      for (let index = 0; index < this.requiredExtras(); index += 1) {
        const variant = this.selectionVariant(this.selections[index]);
        items.push({
          id: Number(variant.id),
          quantity: 1,
          properties: this.bundleLineProperties(bundleId, `extra-${index + 1}`),
        });
      }

      const cart =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification');

      if (cart && typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }

      const body = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        const sectionIds = cart.getSectionsToRender().map((section) => section.id).filter(Boolean);
        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }
      }

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add this bundle to cart.');
        }

        if (cart && typeof cart.renderContents === 'function' && data.sections) {
          cart.renderContents(data);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        this.showError(error.message || 'Unable to add this bundle to cart. Please try again.');
      } finally {
        this.setSubmitting(false);
      }
    }
  }

  const boot = (scope = document) => {
    scope.querySelectorAll?.('[data-smm-root]').forEach((root) => {
      if (root.dataset[READY_FLAG] === 'true') return;
      root.dataset[READY_FLAG] = 'true';
      const instance = new SMMBundle(root);
      root.smmBundle = instance;
    });
  };

  window.SMMBundleBoot = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
